import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PollsPage = () => {
  const [polls, setPolls] = useState([]);
  const [votedPolls, setVotedPolls] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolls = async () => {
      try {
        const response = await axios.get(`${API}/polls`);
        setPolls(response.data);
        
        const voted = JSON.parse(localStorage.getItem('votedPolls') || '{}');
        setVotedPolls(voted);
      } catch (error) {
        console.error('Error fetching polls:', error);
        toast.error('Failed to load polls');
      } finally {
        setLoading(false);
      }
    };

    fetchPolls();
  }, []);

  const handleVote = async (pollId, option) => {
    if (votedPolls[pollId]) {
      toast.info('You already voted in this poll!');
      return;
    }

    try {
      const response = await axios.post(`${API}/polls/vote`, {
        poll_id: pollId,
        option: option
      });

      setPolls(polls.map(p => p.poll_id === pollId ? response.data : p));
      
      const newVoted = { ...votedPolls, [pollId]: option };
      setVotedPolls(newVoted);
      localStorage.setItem('votedPolls', JSON.stringify(newVoted));
      
      toast.success('Vote recorded!');
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to record vote');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="polls-page">
      <div className="bg-[#001f3f] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase heading-font" data-testid="polls-page-title">
            Interactive Polls
          </h1>
          <p className="text-xl text-slate-300 mt-4">Make your voice heard! Vote on SWAC and HBCU topics</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-12" data-testid="loading">Loading polls...</div>
        ) : polls.length === 0 ? (
          <div className="text-center py-12 text-slate-600" data-testid="no-polls">
            No active polls right now. Check back soon!
          </div>
        ) : (
          <div className="space-y-8">
            {polls.map(poll => (
              <PollCard 
                key={poll.poll_id} 
                poll={poll} 
                hasVoted={!!votedPolls[poll.poll_id]}
                userVote={votedPolls[poll.poll_id]}
                onVote={handleVote}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PollCard = ({ poll, hasVoted, userVote, onVote }) => {
  const totalVotes = Object.values(poll.votes).reduce((sum, count) => sum + count, 0);

  const getPercentage = (option) => {
    if (totalVotes === 0) return 0;
    return Math.round((poll.votes[option] / totalVotes) * 100);
  };

  return (
    <div className="bg-white shadow-lg p-8 rounded-lg" data-testid={`poll-${poll.poll_id}`}>
      <div className="flex items-start justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#001f3f] heading-font" data-testid="poll-question">{poll.question}</h3>
        <BarChart3 className="h-6 w-6 text-[#007AFF]" />
      </div>

      <div className="space-y-3">
        {poll.options.map(option => (
          <div key={option} className="relative" data-testid={`poll-option-${option.toLowerCase().replace(/\s+/g, '-')}`}>
            {hasVoted ? (
              <div className="relative overflow-hidden border-2 border-slate-200 rounded p-4">
                <div 
                  className="poll-progress"
                  style={{ width: `${getPercentage(option)}%` }}
                ></div>
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{option}</span>
                    {userVote === option && (
                      <CheckCircle className="h-5 w-5 text-[#007AFF]" data-testid="user-vote-indicator" />
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="font-bold text-[#007AFF]">{getPercentage(option)}%</span>
                    <span className="text-sm text-slate-600">({poll.votes[option]} votes)</span>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => onVote(poll.poll_id, option)}
                className="w-full text-left p-4 border-2 border-slate-200 rounded hover:border-[#007AFF] hover:bg-slate-50 transition-all duration-200 font-medium"
                data-testid={`vote-button-${option.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {option}
              </button>
            )}
          </div>
        ))}
      </div>

      {hasVoted && (
        <div className="mt-4 text-sm text-slate-600 text-center" data-testid="total-votes">
          Total votes: {totalVotes}
        </div>
      )}
    </div>
  );
};

export default PollsPage;